import { useEffect, useMemo, useRef, useState } from 'react';

import type { CategoryNode } from '@lifetimeline/shared';

import { Button } from '../../components/Button.js';
import { Spinner } from '../../components/Spinner.js';
import { useI18n } from '../../i18n/index.js';
import { loadCatalog, sampleNames, search, type LoadedCatalog } from '../../lib/catalog.js';
import styles from './CategorySearch.module.css';

interface CategorySearchProps {
  /** 이미 고른 카테고리. 중복 선택을 막고 표시한다 */
  pickedCategoryIds: ReadonlySet<string>;
  onSelect: (category: CategoryNode) => void;
  onRequestCategory: (query: string) => void;
}

/** placeholder에 돌려 보여줄 예시 개수와 교체 주기 */
const EXAMPLE_COUNT = 6;
const EXAMPLE_INTERVAL_MS = 2200;

/**
 * 카테고리 검색.
 *
 * **목록을 먼저 보여주지 않는다.** 194개를 늘어놓으면 사람들이 자기가 좋아하는
 * 것을 고르는 게 아니라 **눈에 보이는 것 중에서** 고른다. 검색을 거치게 하면
 * "내가 뭘 고르려는지"를 먼저 떠올리게 되고, 선택이 의도적이 된다.
 *
 * 대신 placeholder에 예시를 돌린다 — 고를 수 있는 목록이 아니라 힌트라서
 * 위의 의도를 깨지 않으면서도, 처음 온 사람이 빈 입력창 앞에서 멈추지 않는다.
 *
 * 서버를 부르지 않는다 — 정적 카탈로그를 받아 메모리에서 찾는다. **어떤 키워드로
 * 걸렸는지**도 함께 보여준다("기계식"으로 찾았는데 "키보드"가 나오는 이유).
 */
export function CategorySearch({
  pickedCategoryIds,
  onSelect,
  onRequestCategory,
}: CategorySearchProps) {
  const { t, language } = useI18n();
  const [catalog, setCatalog] = useState<LoadedCatalog | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    loadCatalog(language)
      .then((loaded) => {
        if (!cancelled) {
          setCatalog(loaded);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  /*
   * 열자마자 검색창에 커서를 둔다. 카테고리를 찾는 길이 검색뿐이라 열린 다음
   * 할 일이 타이핑 하나뿐이다 — 한 번 더 누르게 할 이유가 없다.
   *
   * ⚠️ `catalog`가 준비된 **뒤에** 걸어야 한다. 카탈로그를 받는 동안에는
   * 스피너만 그리므로 입력창이 아직 없고, `[]`로 걸면 그때 한 번 돌고 끝나
   * 커서가 아무 데도 안 간다.
   *
   * 시트가 이 포커스를 덮어쓰지 않는다(Sheet.tsx 주석 참조).
   * 모바일에서 키보드까지 올라올지는 브라우저가 정한다 — iOS는 사용자 제스처
   * 밖에서 연 포커스에 키보드를 안 띄우기도 한다. 그래도 커서는 들어가 있다.
   */
  useEffect(() => {
    if (catalog !== null) {
      inputRef.current?.focus();
    }
  }, [catalog]);

  const results = useMemo(() => {
    if (catalog === null || query.trim() === '') {
      return [];
    }
    return search(catalog, query);
  }, [catalog, query]);

  // 질의가 바뀌면 키보드 선택 위치를 처음으로 되돌린다
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const example = useRotatingExample(catalog);

  if (failed) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyText}>{t('error.network')}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (catalog === null) {
    return (
      <div className={styles.emptyState}>
        <Spinner label={t('common.loading')} />
      </div>
    );
  }

  const searching = query.trim() !== '';

  return (
    <div>
      <div className={styles.searchBox}>
        <span className={styles.icon} aria-hidden="true">
          ⌕
        </span>
        <input
          ref={inputRef}
          className={styles.input}
          type="search"
          value={query}
          // 예시는 눈으로 보는 힌트다. aria-label은 고정해서 스크린 리더가
          // 2초마다 바뀌는 문구를 다시 읽지 않게 한다.
          placeholder={example === null ? '' : t('category.searchPlaceholder', { example })}
          aria-label={t('category.searchTitle')}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            /*
             * ⚠️ **조합 중에는 아무것도 하지 않는다.**
             *
             * 한글을 치면 마지막 글자가 조합 중(`애니메이션`의 `션`)이고, 그때
             * 누르는 Enter는 "이 글자를 확정하라"는 뜻이지 "이걸 고르겠다"가
             * 아니다. 그걸 선택으로 받으면 두 가지가 한꺼번에 어긋난다 —
             * 아직 덜 친 상태로 카테고리가 골라지고, 확정되지 못한 글자가
             * 그 다음에 포커스를 받은 칸(픽 이름)에 들어가 앉는다.
             *
             * 영어는 조합이 없어 이 경로를 타지 않는다. 그래서 한글로 칠 때만
             * "가끔" 이상하게 보였다.
             */
            if (event.nativeEvent.isComposing) {
              return;
            }

            // 화살표로 고르고 Enter로 선택 — 손을 키보드에서 떼지 않게
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((current) => Math.min(current + 1, results.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((current) => Math.max(current - 1, 0));
            } else if (event.key === 'Enter') {
              const chosen = results[activeIndex]?.node;
              if (chosen !== undefined && !pickedCategoryIds.has(chosen.id)) {
                event.preventDefault();
                onSelect(chosen);
              }
            }
          }}
        />
      </div>

      {!searching ? (
        <p className={styles.hint}>{t('category.searchHint')}</p>
      ) : results.length > 0 ? (
        <ul className={styles.list}>
          {results.map((result, index) => (
            <li key={result.node.id}>
              <button
                type="button"
                className={styles.row}
                data-active={index === activeIndex}
                disabled={pickedCategoryIds.has(result.node.id)}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => onSelect(result.node)}
              >
                <span className={styles.rowMain}>
                  <span className={styles.rowName}>{result.node.name}</span>
                  {result.matchedOn === 'keyword' ? (
                    // 이름이 아니라 키워드로 걸렸을 때만 이유를 보여준다.
                    // "기계식"을 쳤는데 "키보드"가 나오는 것이 설명 없이는 뜬금없다.
                    <span className={styles.rowPath}>
                      {t('category.matchedVia', { keyword: result.matchedText })}
                    </span>
                  ) : null}
                </span>
                {pickedCategoryIds.has(result.node.id) ? (
                  <span className={styles.badge}>{t('category.alreadyPicked')}</span>
                ) : null}
              </button>
            </li>
          ))}
          {/*
            결과가 **있어도** 요청할 길을 둔다. 찾던 것과 비슷한 게 몇 개
            나왔다고 해서 찾던 그것이 있는 건 아니다 — 여기서 길이 끊기면
            사람들은 아무거나 고르거나 그냥 나간다. 둘 다 없는 카테고리가
            있다는 사실을 우리가 모르게 된다.

            결과 아래, 조용하게 둔다. 목록과 경쟁하면 안 된다.
          */}
          <li>
            <button
              type="button"
              className={styles.requestRow}
              onClick={() => onRequestCategory(query)}
            >
              {t('category.requestMore')}
            </button>
          </li>
        </ul>
      ) : (
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>{t('category.noResults', { query })}</p>
          <Button variant="outline" onClick={() => onRequestCategory(query)}>
            {t('category.requestCta')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * placeholder에 넣을 예시를 주기적으로 바꾼다.
 *
 * 카탈로그가 오기 전에는 null이다 — 그때 예시를 하드코딩해두면 실제 카테고리와
 * 어긋날 수 있고(이름이 바뀌거나 빠질 수 있다), 검색해도 안 나오는 예시를
 * 보여주는 셈이 된다.
 */
function useRotatingExample(catalog: LoadedCatalog | null): string | null {
  const examples = useMemo(
    () => (catalog === null ? [] : sampleNames(catalog, EXAMPLE_COUNT)),
    [catalog],
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (examples.length <= 1) {
      return;
    }
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % examples.length);
    }, EXAMPLE_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [examples]);

  return examples[index] ?? null;
}
